import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { GiftIcon as Gift, CheckCircleIcon as CheckCircle, ClockIcon as Clock, XIcon as X } from "@phosphor-icons/react";
import { isNativeApp } from "@/lib/nativeBridge";

/**
 * Referral credits ledger card — renders on `/share-kennel` AND
 * `/billing`. Shows the user's available + consumed + pending credits
 * and lets them redeem each earned credit (extends their active sub or
 * banks as pending for next subscribe).
 *
 * Hides itself silently when the user has zero credits AND zero referral
 * uses so it doesn't clutter a brand-new account.
 */
export default function ReferralCreditsCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const native = isNativeApp();

  const load = () => {
    if (native) return;
    api.get("/me/referral-credits", { silent: true })
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  };
  useEffect(load, []);

  if (native || !data) return null;
  const { credits, summary } = data;

  // If user has no credits AT ALL, hide. The Share-my-kennel page
  // already explains the program; an empty card here would just be noise.
  if (!credits.length) return null;

  const earned = credits.filter((c) => c.status === "earned");
  const pending = credits.filter((c) => c.status === "pending");
  const consumed = credits.filter((c) => c.status === "consumed");
  const expired = credits.filter((c) => c.status === "expired");

  const redeem = async (creditId) => {
    setBusy(creditId);
    try {
      const { data: r } = await api.post(`/me/referral-credits/${creditId}/redeem`);
      if (r.applied === "subscription_extension") {
        toast.success(`Applied! Your subscription is now extended through ${new Date(r.new_expires_at).toLocaleDateString()}.`);
      } else {
        toast.success("Banked for your next subscription — your free time will apply on first paid period.");
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
    catch { return ""; }
  };

  return (
    <section
      className="rounded-2xl border-2 border-[#E8D9A6] bg-[#FBF7EC] p-5 sm:p-6"
      data-testid="referral-credits-card"
    >
      <div className="flex items-start gap-3 mb-4">
        <span className="p-2 rounded-full bg-white shrink-0">
          <Gift size={20} weight="duotone" className="text-gold-dark" />
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="font-spectral text-xl tracking-tight">
            Your referral credits
          </h3>
          <p className="text-[12.5px] text-[#6B6B6B] mt-0.5">
            {summary.months_available > 0
              ? <>You have <strong className="text-ink">{summary.months_available} month{summary.months_available === 1 ? "" : "s"} free</strong> ready to apply.</>
              : summary.months_consumed > 0
                ? <>You've used <strong className="text-ink">{summary.months_consumed} month{summary.months_consumed === 1 ? "" : "s"}</strong> of referral credit. Refer more breeders to earn more.</>
                : "Refer 3 breeders to earn your first month free. Refer 10 for 3 months. Refer 25 for a full year."}
          </p>
        </div>
      </div>

      {earned.length > 0 && (
        <ul className="space-y-2 mb-3" data-testid="referral-credits-earned">
          {earned.map((c) => (
            <li
              key={c.id}
              className="flex items-center justify-between gap-3 bg-white rounded-lg p-3 border border-[#E8D9A6]"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Gift size={16} weight="fill" className="text-gold-dark shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-bold text-ink">
                    {c.months_value} month{c.months_value === 1 ? "" : "s"} free
                  </p>
                  <p className="text-[11px] text-[#6B6B6B]">
                    Earned at {c.threshold_uses} signups · expires {fmtDate(c.expires_at)}
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => redeem(c.id)}
                disabled={busy === c.id}
                data-testid={`referral-credit-redeem-${c.id}`}
                className="rounded-full bg-ink hover:bg-crimson text-white whitespace-nowrap"
              >
                {busy === c.id ? "Applying…" : "Apply now"}
              </Button>
            </li>
          ))}
        </ul>
      )}

      {pending.length > 0 && (
        <div className="bg-white/60 rounded-lg p-3 mb-2 border border-dashed border-[#E8D9A6]" data-testid="referral-credits-pending">
          <p className="text-[12px] flex items-start gap-2">
            <Clock size={14} weight="duotone" className="text-gold-dark mt-0.5 shrink-0" />
            <span className="text-[#3F3F3F]">
              <strong className="text-ink">
                {pending.reduce((s, c) => s + c.months_value, 0)} month{pending.reduce((s, c) => s + c.months_value, 0) === 1 ? "" : "s"}
              </strong>{" "}
              banked — will apply automatically the first time you (re)subscribe to a paid plan.
            </span>
          </p>
        </div>
      )}

      {(consumed.length > 0 || expired.length > 0) && (
        <details className="mt-3">
          <summary className="text-[11px] uppercase tracking-[0.14em] font-bold text-[#6B6B6B] cursor-pointer hover:text-ink">
            History · {consumed.length} used · {expired.length} expired
          </summary>
          <ul className="mt-2 space-y-1">
            {consumed.map((c) => (
              <li key={c.id} className="flex items-center gap-2 text-[12px] text-[#6B6B6B]">
                <CheckCircle size={12} weight="fill" className="text-[#0F8043] shrink-0" />
                <span>{c.months_value} month{c.months_value === 1 ? "" : "s"} applied {c.consumed_at ? `on ${fmtDate(c.consumed_at)}` : ""}</span>
              </li>
            ))}
            {expired.map((c) => (
              <li key={c.id} className="flex items-center gap-2 text-[12px] text-[#9B9B9B]">
                <X size={12} weight="bold" className="shrink-0" />
                <span>{c.months_value} month{c.months_value === 1 ? "" : "s"} expired {c.expired_at ? `on ${fmtDate(c.expired_at)}` : ""}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
